import { supabase } from '@/integrations/supabase/client';
import { EdgeFunctionResponse, IntegrationError, ApiConfig } from './types';
import { useCreditErrorHandler } from '@/hooks/errors/use-credit-error-handler';

export abstract class BaseEdgeFunctionClient {
  protected config: ApiConfig;
  private creditErrorHandler?: ReturnType<typeof useCreditErrorHandler>;

  constructor(config: ApiConfig = {}) {
    this.config = {
      timeout: 30000,
      retries: 2,
      retryDelay: 1000,
      ...config,
    };
  }

  /**
   * Set the credit error handler (should be called from React components)
   */
  public setCreditErrorHandler(handler: ReturnType<typeof useCreditErrorHandler>) {
    this.creditErrorHandler = handler;
  }

  /**
   * Invoke a Supabase Edge Function with error handling, retries, and automatic cache invalidation
   */
  protected async invokeEdgeFunction<T = any>(
    functionName: string,
    body: any,
    options: { retries?: number; skipCacheInvalidation?: boolean } = {}
  ): Promise<T> {
    const maxRetries = options.retries ?? this.config.retries ?? 2;
    const skipCacheInvalidation = options.skipCacheInvalidation ?? false;
    let lastError: Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const { data, error }: EdgeFunctionResponse<T> = await supabase.functions.invoke(
          functionName,
          { body }
        );

        if (error) {
          // Check if this is a credit error and handle it specially
          if (error.status === 402 && this.creditErrorHandler?.isInsufficientCreditsError(error)) {
            this.creditErrorHandler.handleCreditError(error);
          }
          
          throw new IntegrationError(
            error.message || `Edge function ${functionName} failed`,
            'EDGE_FUNCTION_ERROR',
            error.status,
            error
          );
        }

        // Automatically invalidate AI usage cache for successful AI operations
        if (!skipCacheInvalidation && this.isAIOperation(functionName)) {
          this.triggerCacheInvalidation(true); // Hard invalidation for immediate feedback
          console.log(`🔄 AI usage cache invalidated after successful ${functionName} operation`);
        }

        return data;
      } catch (error: any) {
        lastError = error;
        
        // Don't retry on certain errors
        if (this.shouldNotRetry(error) || attempt === maxRetries) {
          break;
        }

        // Wait before retrying
        if (attempt < maxRetries) {
          await this.delay(this.config.retryDelay! * Math.pow(2, attempt));
        }
      }
    }

    throw new IntegrationError(
      lastError.message || `Failed to invoke ${functionName} after ${maxRetries + 1} attempts`,
      'MAX_RETRIES_EXCEEDED',
      undefined,
      lastError
    );
  }

  /**
   * Determine if an error should not be retried
   */
  private shouldNotRetry(error: any): boolean {
    // Don't retry authentication errors, validation errors, etc.
    const nonRetryableErrors = [401, 403, 422, 400];
    return nonRetryableErrors.includes(error.status);
  }

  /**
   * Delay utility for retries
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if the function name represents an AI operation that consumes credits
   */
  private isAIOperation(functionName: string): boolean {
    const aiOperations = [
      'agents',
      'text-generation', 
      'threaded-text-generation',
      'generate-image',
      'research-perplexity'
    ];
    return aiOperations.includes(functionName);
  }

  /**
   * Trigger cache invalidation event for AI usage data
   */
  private triggerCacheInvalidation(immediate: boolean = true): void {
    try {
      // Dispatch custom event that the useAIUsageData hook listens for
      const event = new CustomEvent('ai-usage-cache-invalidate', {
        detail: { immediate }
      });
      window.dispatchEvent(event);
    } catch (error) {
      // Fail silently - cache invalidation is not critical for functionality
      console.warn('Failed to trigger cache invalidation event:', error);
    }
  }

  /**
   * Handle common error scenarios
   */
  protected handleError(error: any, context: string): never {
    if (error instanceof IntegrationError) {
      throw error;
    }

    throw new IntegrationError(
      `${context}: ${error.message || 'Unknown error'}`,
      'INTEGRATION_ERROR',
      error.status,
      error
    );
  }
} 